/**
 * @coeldery/document-scan / detect.ts —— **文件四角偵測**（純函數、零依賴、零 DOM）
 *
 * ⚠️ v1（Otsu 全圖亮度 + 最大亮區）太脆弱：背景／牆／反光有亮區時「最大亮區」＝成個畫面 → 爆框。
 *    → v2 **邊緣圍封法**（唔靠絕對亮度）：Sobel 邊 → 膨脹補邊 → 由畫面**四邊 flood fill**「背景」→
 *      「被邊圍住」嘅**最大連通區**＝文件 → 凸包 → **min/max(x±y) 極值角**；
 *      太大（> `maxAreaRatio`）／太細 → **回 null**（寧願唔畫，都唔畫爆框）。
 * 局限：文件要同畫面四邊有少少邊界；邊太弱會漏（→ null）；背景比文件光都 OK。
 */

export interface Point { x: number; y: number }
/** 極簡影像（ImageData 相容）：RGBA，`data.length === width * height * 4` */
export interface Raster { data: Uint8ClampedArray | Uint8Array; width: number; height: number }

/** RGBA → 灰階（0–255；Rec.601）*/
export function toGray(r: Raster): Uint8Array {
  const { data } = r;
  const g = new Uint8Array(r.width * r.height);
  for (let i = 0, p = 0; i < g.length; i++, p += 4) {
    g[i] = (data[p] * 299 + data[p + 1] * 587 + data[p + 2] * 114) / 1000;
  }
  return g;
}

/** 3×3 方框模糊（去噪，令二值化更穩）*/
export function boxBlur(gray: Uint8Array, width: number, height: number): Uint8Array {
  const out = new Uint8Array(gray.length);
  const cl = (v: number, hi: number) => (v < 0 ? 0 : v > hi ? hi : v);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let s = 0;
      for (let dy = -1; dy <= 1; dy++) {
        const yy = cl(y + dy, height - 1);
        for (let dx = -1; dx <= 1; dx++) s += gray[yy * width + cl(x + dx, width - 1)];
      }
      out[y * width + x] = s / 9;
    }
  }
  return out;
}

/** Otsu 最佳門檻（0–255）*/
export function otsu(gray: Uint8Array): number {
  const hist = new Array<number>(256).fill(0);
  for (let i = 0; i < gray.length; i++) hist[gray[i]]++;
  const total = gray.length;
  let sum = 0;
  for (let t = 0; t < 256; t++) sum += t * hist[t];
  let sumB = 0, wB = 0, best = -1, thr = 127;
  for (let t = 0; t < 256; t++) {
    wB += hist[t];
    if (!wB) continue;
    const wF = total - wB;
    if (!wF) break;
    sumB += t * hist[t];
    const mB = sumB / wB, mF = (sum - sumB) / wF;
    const between = wB * wF * (mB - mF) * (mB - mF);
    if (between > best) { best = between; thr = t; }
  }
  return thr;
}

/** Sobel 邊緣強度（0–255；`/4` 縮放 + clamp）*/
export function sobelMag(gray: Uint8Array, width: number, height: number): Uint8Array {
  const g = new Uint8Array(gray.length);
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const i = y * width + x;
      const gx = -gray[i - width - 1] - 2 * gray[i - 1] - gray[i + width - 1]
        + gray[i - width + 1] + 2 * gray[i + 1] + gray[i + width + 1];
      const gy = -gray[i - width - 1] - 2 * gray[i - width] - gray[i - width + 1]
        + gray[i + width - 1] + 2 * gray[i + width] + gray[i + width + 1];
      const m = Math.hypot(gx, gy) / 4;
      g[i] = m > 255 ? 255 : m;
    }
  }
  return g;
}

/** 3×3 膨脹（補邊隙，令圍封唔漏）*/
export function dilate(bin: Uint8Array, width: number, height: number): Uint8Array {
  const out = new Uint8Array(bin.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let on = 0;
      for (let dy = -1; dy <= 1 && !on; dy++) {
        const yy = y + dy; if (yy < 0 || yy >= height) continue;
        for (let dx = -1; dx <= 1; dx++) {
          const xx = x + dx; if (xx < 0 || xx >= width) continue;
          if (bin[yy * width + xx]) { on = 1; break; }
        }
      }
      out[y * width + x] = on;
    }
  }
  return out;
}

/** 由畫面**四邊** flood fill（只穿過「非邊」像素）→ 標記「可連到框」嘅背景 */
export function floodOutside(edge: Uint8Array, width: number, height: number): Uint8Array {
  const out = new Uint8Array(edge.length);
  const stack: number[] = [];
  const push = (i: number) => { if (!edge[i] && !out[i]) { out[i] = 1; stack.push(i); } };
  for (let x = 0; x < width; x++) { push(x); push((height - 1) * width + x); }
  for (let y = 0; y < height; y++) { push(y * width); push(y * width + width - 1); }
  while (stack.length) {
    const i = stack.pop() as number;
    const x = i % width, y = (i / width) | 0;
    if (x > 0) push(i - 1);
    if (x + 1 < width) push(i + 1);
    if (y > 0) push(i - width);
    if (y + 1 < height) push(i + width);
  }
  return out;
}

/** mask=1 嘅最大連通區（4-連通），回像素點；冇 → 空陣列 */
export function largestComponent(mask: Uint8Array, width: number, height: number): Point[] {
  const seen = new Uint8Array(mask.length);
  const stack: number[] = [];
  let best: Point[] = [];
  for (let start = 0; start < mask.length; start++) {
    if (!mask[start] || seen[start]) continue;
    const blob: Point[] = [];
    stack.length = 0; stack.push(start); seen[start] = 1;
    while (stack.length) {
      const idx = stack.pop() as number;
      const x = idx % width, y = (idx / width) | 0;
      blob.push({ x, y });
      if (x > 0 && mask[idx - 1] && !seen[idx - 1]) { seen[idx - 1] = 1; stack.push(idx - 1); }
      if (x + 1 < width && mask[idx + 1] && !seen[idx + 1]) { seen[idx + 1] = 1; stack.push(idx + 1); }
      if (y > 0 && mask[idx - width] && !seen[idx - width]) { seen[idx - width] = 1; stack.push(idx - width); }
      if (y + 1 < height && mask[idx + width] && !seen[idx + width]) { seen[idx + width] = 1; stack.push(idx + width); }
    }
    if (blob.length > best.length) best = blob;
  }
  return best;
}

/** Andrew monotone-chain 凸包（逆時針；無重複端點）*/
export function convexHull(pts: Point[]): Point[] {
  if (pts.length < 3) return pts.slice();
  const p = pts.slice().sort((a, b) => a.x - b.x || a.y - b.y);
  const cross = (o: Point, a: Point, b: Point) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const lower: Point[] = [];
  for (const pt of p) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], pt) <= 0) lower.pop();
    lower.push(pt);
  }
  const upper: Point[] = [];
  for (let i = p.length - 1; i >= 0; i--) {
    const pt = p[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], pt) <= 0) upper.pop();
    upper.push(pt);
  }
  lower.pop(); upper.pop();
  return lower.concat(upper);
}

/** 由凸包攞 4 個極值角 → [tl, tr, br, bl]（文件視為凸四邊形）*/
export function quadFromHull(hull: Point[]): Point[] {
  let tl = hull[0], tr = hull[0], br = hull[0], bl = hull[0];
  let minSum = Infinity, maxSum = -Infinity, maxDiff = -Infinity, minDiff = Infinity;
  for (const p of hull) {
    const s = p.x + p.y, d = p.x - p.y;
    if (s < minSum) { minSum = s; tl = p; }
    if (s > maxSum) { maxSum = s; br = p; }
    if (d > maxDiff) { maxDiff = d; tr = p; }
    if (d < minDiff) { minDiff = d; bl = p; }
  }
  return [tl, tr, br, bl];
}

/** 亮度值分位數（0–255；`p` 例如 0.9）*/
export function percentile(v: Uint8Array, p: number): number {
  const hist = new Int32Array(256);
  for (let i = 0; i < v.length; i++) hist[v[i]]++;
  const target = v.length * p;
  let acc = 0;
  for (let t = 0; t < 256; t++) { acc += hist[t]; if (acc >= target) return t; }
  return 255;
}

/** 四邊形面積（shoelace）*/
export function quadArea(q: Point[]): number {
  let a = 0;
  for (let i = 0; i < q.length; i++) { const p = q[i], n = q[(i + 1) % q.length]; a += p.x * n.y - n.x * p.y; }
  return Math.abs(a) / 2;
}

/**
 * 由 mask 攞「最大連通區 → 凸包 → 四角」，並用**凸包面積**做大小檢查。
 * 凸包面積對「實心區（圍封區）」同「線圈（邊輪廓）」都成立。
 */
function quadFromMask(mask: Uint8Array, w: number, h: number, minRatio: number, maxRatio: number): Point[] | null {
  const blob = largestComponent(mask, w, h);
  if (blob.length < 24) return null;
  const quad = quadFromHull(convexHull(blob));
  const frac = quadArea(quad) / (w * h);
  if (frac < minRatio || frac > maxRatio) return null;
  const wSide = Math.hypot(quad[1].x - quad[0].x, quad[1].y - quad[0].y);
  const hSide = Math.hypot(quad[3].x - quad[0].x, quad[3].y - quad[0].y);
  if (wSide < w * 0.25 || hSide < h * 0.25) return null;
  return quad;
}

/** 主入口：偵測文件四角（唔肯定 → `null`）
 *  ① 首選 **圍封法**（被邊圍住嘅實心區）＝ 最準
 *  ② 後備 **邊輪廓凸包**（圍封漏咗／邊有缺口時補救）
 */
export function detectCorners(
  raster: Raster,
  opts: { minAreaRatio?: number; maxAreaRatio?: number } = {},
): Point[] | null {
  const minR = opts.minAreaRatio ?? 0.05;
  const maxR = opts.maxAreaRatio ?? 0.9;
  const { width: w, height: h } = raster;
  if (w < 16 || h < 16) return null;
  const gray = boxBlur(toGray(raster), w, h);
  const grad = sobelMag(gray, w, h);
  // ⚠️ 門檻用 min(otsu, 90 分位) → 比純 otsu 寬容（弱啲嘅邊都收到）；下限 24 防雜訊
  const thr = Math.max(24, Math.min(otsu(grad), percentile(grad, 0.9)));
  const edge = new Uint8Array(grad.length);
  for (let i = 0; i < grad.length; i++) edge[i] = grad[i] > thr ? 1 : 0;
  const dilated = dilate(dilate(edge, w, h), w, h);      // 2× 膨脹補邊隙（圍封少啲漏）
  const outside = floodOutside(dilated, w, h);           // 可連到框 = 背景
  const inside = new Uint8Array(grad.length);            // 被邊圍住 = 候選文件
  for (let i = 0; i < grad.length; i++) inside[i] = !dilated[i] && !outside[i] ? 1 : 0;
  return quadFromMask(inside, w, h, minR, maxR)          // ① 圍封
    ?? quadFromMask(dilated, w, h, minR, maxR);          // ② 邊輪廓凸包（後備）
}
